import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {AnalyticEventCategory, AnalyticEventType, LoginProviderEnum} from '../../../../shared/entity_datas/EntityDatas'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct'
import {UserLoginState} from '../../../../shared/UserLoginState'
import {CA} from '../../../shared/scripts/analytics/Analytics'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {Sound} from '../../../shared/scripts/Sound'
import {UICheckoutAjaxEndPoints} from '../../../shared/scripts/ui_checkout/UICheckoutAjaxEndPoints'
import {UIPopupController} from '../../../shared/scripts/ui_popup/UIPopupController'
import {createButtonIn, createImgIn, createLinkIn, createParagraphIn, setElementInnerHTML} from '../../../shared/scripts/ui_utils/DomUtils'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {UIProfileLinkController} from '../../../shared/scripts/profile/UIProfileLinkController'
import {getNestedTranslation, TranslationObject} from '@colonist/i18n'
import {UIProfileSettingsController} from '../../../shared/scripts/profile/UIProfileSettingsController'
import {PaymentMethod} from '../../../../shared/store/BaseColonistStoreProduct'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables'
import {ViewPopupDiscordSettings} from '../../../shared/scripts/ui_popup/ViewPopupDiscordSettings'
import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils.ts'
import {createTooltipIn} from '../../../shared/scripts/ui_utils/TooltipUtils.ts'

export class UIWProfileSettingsController extends UIProfileSettingsController {
    private readonly username: string
    private readonly membershipStatusContainer: HTMLDivElement

    private readonly profileLinkController: UIProfileLinkController

    private readonly googleAccountLinkButton: HTMLSpanElement
    private readonly discordAccountLinkButton: HTMLSpanElement
    private readonly appleAccountLinkButton: HTMLSpanElement

    private currentUserLoginState: UserLoginState[]
    private profileIsCurrentUser: boolean

    constructor(username: string, profileLinkController: UIProfileLinkController) {
        super()
        this.username = username
        this.profileLinkController = profileLinkController

        this.membershipStatusContainer = document.getElementById('membership-status-container') as HTMLDivElement

        this.googleAccountLinkButton = document.getElementById('page-profile-settings-account-link-google') as HTMLSpanElement
        this.discordAccountLinkButton = document.getElementById('page-profile-settings-account-link-discord') as HTMLSpanElement
        this.appleAccountLinkButton = document.getElementById('page-profile-settings-account-link-apple') as HTMLSpanElement

        this.init()
    }

    private init() {
        this.profileIsCurrentUser = this.isProfileCurrentUser()

        if(!this.profileIsCurrentUser) {
            this.hideSettingsTab()
            this.redirectToMainProfileSection()
            return
        }

        UIWProfileSettingsController.setLinkedAccountButtonAction(this.googleAccountLinkButton, UIAPIEndpoints.googleLink())
        UIWProfileSettingsController.setLinkedAccountButtonAction(this.discordAccountLinkButton, UIAPIEndpoints.discordLink())
        UIWProfileSettingsController.setLinkedAccountButtonAction(this.appleAccountLinkButton, UIAPIEndpoints.appleLink())

        if(ClientUserState.userState?.items.hasMembership()) {
            this.showMembershipTitleAndIcon()
            return
        }

        this.showNotMemberTextAndBecomeMemberCTA()
    }

    setUserLoginState(loginState: UserLoginState[]) {
        this.currentUserLoginState = loginState
        this.setAccountEmailsIfAlreadyLinked()
    }

    private isProfileCurrentUser(): boolean {
        if(ClientUserState.userState == undefined) return false
        return ClientUserState.userState.username.toLowerCase() == decodeURIComponent(this.username.toLowerCase())
    }

    hideSettingsTab() {
        this.profileLinkController.settingsSectionLink.style.display = 'none'
    }

    showSettingsTab() {
        this.profileLinkController.settingsSectionLink.style.display = 'flex'
    }

    private setAccountEmailsIfAlreadyLinked() {
        for(const account of this.currentUserLoginState) {
            const emailText: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: account.email}}
            switch(account.providerId) {
                case LoginProviderEnum.Google: {
                    setElementInnerHTML(this.googleAccountLinkButton, emailText)
                    this.googleAccountLinkButton.classList.add('page-profile-settings-account-text-already-linked')
                    createTooltipIn(this.googleAccountLinkButton, {key: 'strings:profilePage.removeAccount.title'})
                    UIWProfileSettingsController.setUnlinkAccountButtonAction(this.googleAccountLinkButton, UIAPIEndpoints.googleUnlink())
                    break
                }
                case LoginProviderEnum.Discord: {
                    setElementInnerHTML(this.discordAccountLinkButton, emailText)
                    this.discordAccountLinkButton.classList.add('page-profile-settings-account-text-already-linked')
                    createTooltipIn(this.discordAccountLinkButton, {key: 'strings:profilePage.removeAccount.title'})
                    UIWProfileSettingsController.setUnlinkAccountButtonAction(this.discordAccountLinkButton, UIAPIEndpoints.discordUnlink())
                    break
                }
                case LoginProviderEnum.Apple: {
                    setElementInnerHTML(this.appleAccountLinkButton, emailText)
                    this.appleAccountLinkButton.classList.add('page-profile-settings-account-text-already-linked')
                    createTooltipIn(this.appleAccountLinkButton, {key: 'strings:profilePage.removeAccount.title'})
                    UIWProfileSettingsController.setUnlinkAccountButtonAction(this.appleAccountLinkButton, UIAPIEndpoints.appleUnlink())
                    break
                }
            }
        }
    }

    private static setLinkedAccountButtonAction(element: HTMLSpanElement, apiLink: string) {
        element.onclick = () => this.handleSettingsClick(() => this.handleLink(apiLink))
    }

    private static setUnlinkAccountButtonAction(element: HTMLSpanElement, apiLink: string) {
        element.onclick = () => this.handleSettingsClick(() => this.handleUnlink(apiLink))
    }

    private static handleSettingsClick(action: () => void) {
        if(UIUtilsEjsVariables.isUserOnDiscord) {
            new ViewPopupDiscordSettings()
            return
        }
        action()
    }

    private static handleLink(apiLink: string) {
        navigateToPage(apiLink)
    }

    private static async handleUnlink(apiLink: string) {
        UIPopupController.create({key: 'strings:profilePage.removeAccount.title'}, {key: 'strings:profilePage.removeAccount.body'}, async() => {
            const response = await UIAjaxWrapper.getRequestHandler(apiLink, undefined)
            if(response == undefined) return
            location.reload()
        }, () => undefined)
    }

    private redirectToMainProfileSection() {
        if(this.profileLinkController.activeLink != this.profileLinkController.settingsSectionLink) return
        navigateToPage(`/profile/${this.username}`)
    }

    private showMembershipTitleAndIcon() {
        const currentMembership = ClientUserState.userState?.items.getCurrentMembership()
        if(currentMembership == undefined) return
        const currentMembershipProduct = ColonistMembershipProduct.getMembershipProduct(currentMembership.type)

        createImgIn(this.membershipStatusContainer, currentMembership.image)

        const membershipTitleText = getNestedTranslation({key: 'strings:storePage.membership.body'}, {
            title: currentMembership.title,
            frequency: currentMembershipProduct.billedAnnually ? {key: 'strings:storePage.membership.frequency.annual'} : {key: 'strings:storePage.membership.frequency.monthly'},
        })
        const membershipTitleElement = createParagraphIn(this.membershipStatusContainer, membershipTitleText, '')
        this.membershipStatusContainer.appendChild(membershipTitleElement)

        const canManage = (ClientUserState.userState?.items.membershipPaymentMethod == PaymentMethod.Stripe) || (ClientUserState.userState?.items.membershipPaymentMethod == PaymentMethod.Discord)
        if(canManage) {
            const membershipManageButton = createButtonIn(this.membershipStatusContainer, 'button-manage-membership', {key: 'strings:storePage.sections.membership.action.manage'})
            membershipManageButton.onclick = () => UIWProfileSettingsController.handleSettingsClick(() => this.manageMembershipClickAction())

            this.membershipStatusContainer.appendChild(membershipManageButton)
        }
    }

    private manageMembershipClickAction() {
        Sound.playSound(Sound.SoundType.Click)
        CA.sendGoogleAnalyticsEvent(CA.Category.Profile, CA.Label.ClickedProfileManageMembership, ClientUserState.userState?.items.membership)

        if(this.profileIsCurrentUser) void UICheckoutAjaxEndPoints.executeStripeCustomerPortalSession()
    }

    private showNotMemberTextAndBecomeMemberCTA() {
        const notMemberText = createParagraphIn(this.membershipStatusContainer, {key: 'strings:profilePage.settings.membership.dontHaveMemebership'}, '')
        const becomeMemberCTA = createLinkIn(this.membershipStatusContainer, '/store#membership', {key: 'strings:profilePage.settings.membership.becomeMember'}, 'button_cta_main')
        becomeMemberCTA.onclick = () => UIWProfileSettingsController.becomeMemberClickAction()

        this.membershipStatusContainer.appendChild(notMemberText)
        this.membershipStatusContainer.appendChild(becomeMemberCTA)
    }

    static becomeMemberClickAction() {
        Sound.playSound(Sound.SoundType.Click)
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ClickedStore,
            category: AnalyticEventCategory.Store,
        }
        void CA.sendAnalyticEvent(eventData)
    }
}
